import Head from "next/head";
import type { NextPage } from "next";
import { wrapper } from "@app/store/store";
import Template from "@app/Template/Template";
import { BlogCatalogue } from "@widgets/blog-catalogue";
import { blogApi, BlogPostPreview } from "@entities/blog";
import { PROJECT_NAME } from "@shared/config/GlobalText";
import { configApi } from "@shared/lib/configApi";
import { RouterConfig } from "@shared/lib/routerConfig";
import { CustomHead, DEFAULT_OG_IMAGE } from "@shared/ui/CustomHead";

interface BlogPageProps {
    posts: BlogPostPreview[];
    pagesCount: number;
    currentPage: number;
}

const Blog: NextPage<BlogPageProps> = ({ posts, pagesCount, currentPage }) => {
    const pageSuffix = currentPage > 1 ? ` | Страница ${currentPage}` : "";
    const title = `Автоблог ${PROJECT_NAME} — авто новости, советы, обзоры${pageSuffix}`;
    const descriptionPageSuffix = currentPage > 1 ? ` Страница ${currentPage}.` : "";
    const description = `Блог ${PROJECT_NAME} — свежие новости автопрома, полезные советы автовладельцам, обзоры автомобилей и лайфхаки по уходу за авто.${descriptionPageSuffix}`;
    const baseUrl = `${configApi.API_PUBLIC_DOMAIN}${RouterConfig.BLOG}`;
    const canonical = currentPage > 1 ? `${baseUrl}?page=${currentPage}` : baseUrl;
    const linkRelPrev =
        currentPage > 2 ? `${baseUrl}?page=${currentPage - 1}` : currentPage === 2 ? baseUrl : null;
    const linkRelNext = currentPage < pagesCount ? `${baseUrl}?page=${currentPage + 1}` : null;

    return (
        <Template>
            <CustomHead
                title={title}
                description={description}
                robots={"index,follow"}
                canonical={canonical}
                openGraph={{
                    title: title,
                    description: description,
                    url: canonical,
                    type: "website",
                    image: {
                        url: DEFAULT_OG_IMAGE,
                        alt: title
                    }
                }}
            />
            <Head>
                {linkRelPrev && <link rel="prev" href={linkRelPrev} />}
                {linkRelNext && <link rel="next" href={linkRelNext} />}
            </Head>
            <BlogCatalogue posts={posts} pagesCount={pagesCount} currentPage={currentPage} />
        </Template>
    );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async (context) => {
    const page = Number(context.query.page) || 1;
    const order = context.query.order === "asc" ? "asc" : "desc";

    const postsResult = await store
        .dispatch(blogApi.endpoints.getBlogPosts.initiate({ page, count: 12, order }))
        .unwrap();

    if (!postsResult) {
        return { notFound: true };
    }

    return {
        props: {
            posts: postsResult.data,
            pagesCount: postsResult.pagesCount,
            currentPage: page
        }
    };
});

export default Blog;